import React, {useEffect, useState, useRef} from 'react'
import {Link} from 'react-router-dom';
import PageApp from '../../../Page';
import { HomeOutlined, UnorderedListOutlined, ExclamationCircleFilled, UploadOutlined, DownloadOutlined } from '@ant-design/icons';
import {Modal, Divider, Button, message, Space, Tooltip, Upload, Alert, Progress} from 'antd';
import {importBulk, importBulkConfirm} from '../../../../apis/ReciboNominaApi';
import './styles.scss';

const { confirm } = Modal;

const routes = [
  {
    href: '',
    title: <>
      <HomeOutlined />
      <span>Inicio</span>
    </>,
  },
  {
    href: '',
    title: (
      <>
        <UnorderedListOutlined />
        <span>Importar Nómina</span>
      </>
    ),
}];

const ImportarNomina = () => {
    const [uploading, setUploading] = useState(false);  
    const [progress, setProgress] = useState(0);
    const progressTimer = useRef(null);

    useEffect(() => {
      return () => {
        if (progressTimer.current) {
          clearInterval(progressTimer.current);
        }
      };
    }, []);

    const handleImport = async (file, _fileList) => {
      const forceSave = false;
      const periodoId = 0;
      const anioPeriodo = 0;
      const numPeriodo = 0;

      setUploading(true);
      setProgress(0);
      if (progressTimer.current) {
        clearInterval(progressTimer.current);
      }
      // Progreso simulado para dar feedback sin cargar al backend
      progressTimer.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + 5;
        });
      }, 300);
      let shouldStopLoading = true;
      
      try {
        const response = await importBulk(
          file,
          periodoId,
          anioPeriodo,
          numPeriodo,
          forceSave
        );

        if (response.ok) {
          const result = response.payload;
          
          const hasErrors = Array.isArray(result.errors) && result.errors.length > 0;

          // Si requiere confirmación (hay errores y no se guardó)
          if (result.requires_confirmation) {
            shouldStopLoading = false;
            Modal.confirm({
              title: 'Confirmar importación con errores',
              icon: <ExclamationCircleFilled />,
              content: (
                <div>
                  <p>{result.message}</p>
                  <p style={{marginTop: 16}}>Total de registros: <strong>{result.total}</strong></p>
                  <p>Registros exitosos: <strong style={{color: '#52c41a'}}>{result.successful}</strong></p>
                  <p>Registros fallidos: <strong style={{color: '#ff4d4f'}}>{result.failed}</strong></p>
                  {result.errors && result.errors.length > 0 && (
                    <>
                      <p style={{marginTop: 16, fontWeight: 'bold'}}>Errores encontrados (primeros 10):</p>
                      <ul style={{maxHeight: 200, overflow: 'auto', fontSize: '12px', background: '#fafafa', padding: 10, borderRadius: 4}}>
                        {result.errors.map((error, index) => (
                          <li key={index} style={{marginBottom: 4}}>{renderErrorWithLink(error)}</li>
                        ))}
                      </ul>
                      {result.total_errors > 10 && (
                        <p style={{fontSize: '12px', color: '#888', marginTop: 8}}>
                          ...y {result.total_errors - 10} errores más
                        </p>
                      )}
                    </>
                  )}
                </div>
              ),
              width: 700,
              okText: 'Sí, guardar registros exitosos',
              cancelText: 'No, cancelar importación',
              onOk: async () => {
                setUploading(true);
                const confirmedResponse = await importBulkConfirm(
                  periodoId,
                  anioPeriodo,
                  numPeriodo,
                  result.cached_records
                );
                
                if (confirmedResponse.ok) {
                  const confirmedResult = confirmedResponse.payload;
                  if (confirmedResult.saved) {
                    message.success(`Se guardaron correctamente ${confirmedResult.successful} recibos de nómina`);
                  }
                }
                setUploading(false);
              },
              onCancel: () => {
                message.info('Importación cancelada');
                setUploading(false);
              }
            });
          } else {
            // No requiere confirmación (todo exitoso o ya se guardó o error de validación)
            const hasValidationIssues = hasErrors || (result.total_errors > 0) || (result.failed > 0);
            
            if (hasValidationIssues) {
              const firstError = Array.isArray(result.errors) && result.errors.length > 0 ? result.errors[0] : 'Error en la importación';
              message.error(renderErrorWithLink(firstError));
              Modal.warning({
                title: result.total === 0 ? 'Error en la importación' : 'Importación completada con errores',
                content: (
                  <div>
                    {result.total > 0 && (
                      <>
                        <p>Total de registros: <strong>{result.total}</strong></p>
                        <p>Exitosos: <strong style={{color: '#52c41a'}}>{result.successful}</strong></p>
                        <p>Fallidos: <strong style={{color: '#ff4d4f'}}>{result.failed}</strong></p>
                      </>
                    )}
                    {result.errors && result.errors.length > 0 && (
                      <>
                        <p style={{marginTop: result.total > 0 ? 16 : 0, fontWeight: 'bold'}}>Errores:</p>
                        <ul style={{maxHeight: 200, overflow: 'auto', fontSize: '12px', background: '#fafafa', padding: 10}}>
                          {result.errors.map((error, index) => (
                            <li key={index}>{renderErrorWithLink(error)}</li>
                          ))}
                        </ul>
                        {result.total_errors > 10 && (
                          <p style={{fontSize: '12px', color: '#888', marginTop: 8}}>
                            ...y {result.total_errors - 10} errores más
                          </p>
                        )}
                      </>
                    )}
                  </div>
                ),
                width: 600,
              });
            } else {
              message.success(`Se importaron correctamente ${result.successful} recibos de nómina`);
            }
          }
        }
      } catch (error) {
        message.error('Error al importar el archivo');
      } finally {
        if (shouldStopLoading) {
          setUploading(false);
        }
        if (progressTimer.current) {
          clearInterval(progressTimer.current);
        }
        setProgress(100);
        setTimeout(() => setProgress(0), 500);
      }

      return false;
    };

    // Función para renderizar errores con enlaces a crear periodo
    const renderErrorWithLink = (error) => {
      // Detectar si el error menciona "crear el periodo"
      const match = error.match(/crear el periodo/);
      if (match) {
        const periodoId = match[1];
        // Usamos <a> en lugar de <Link> porque los mensajes/toasts pueden renderizarse fuera del Router
        return (
          <span>
            El período {periodoId} no existe, es necesario{' '}
            <a href="/admin/periodos" style={{color: '#1890ff', textDecoration: 'underline'}}>
              crear periodo
            </a>
          </span>
        );
      }
      return error;
    };

    const downloadTemplate = () => {
      const csvContent = 'tipo,anio,num_periodo,num_empleado,clave_concepto,monto,exento,dias,horas,saldo_anterior,saldo_nuevo,referencia\n' +
                         'QUINCENAL,2025,1,001,001,5000.00,500.00,7,0,0,0,Pago quincenal\n' +
                         'QUINCENAL,2025,1,001,002,1500.00,0,0,8,0,0,Horas extras\n' +
                         'QUINCENAL,2025,1,002,001,4500.00,450.00,7,0,0,0,Pago quincenal';
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'plantilla_importar_nomina.csv';
      link.click();
    };

    return <PageApp 
      routes={routes}
      title={'Importar Nómina'}>
        <>
          <Alert
            type="info"
            message="Recuerda crear los períodos correspondientes antes de importar la nómina."
            showIcon
          />

          <Space style={{marginTop: 16, marginBottom: 16}}>
            <Upload
              beforeUpload={handleImport}
              accept=".csv,.xlsx,.xls"
              showUploadList={false}
              disabled={uploading}
            >
              <Tooltip 
                title=""
                placement="top"
              >
                <Button 
                  type="primary"
                  icon={<UploadOutlined />} 
                  disabled={uploading}
                  loading={uploading}
                  size="large"
                >
                  Importar Nómina
                </Button>
              </Tooltip>
            </Upload>
            <Button 
              icon={<DownloadOutlined />} 
              onClick={downloadTemplate}
              size="large"
            >
              Descargar Plantilla
            </Button>

          </Space>

          {uploading && (
            <div style={{maxWidth: 400, marginBottom: 16}}>
              <Progress percent={progress} status={progress >= 100 ? 'success' : 'active'} />
            </div>
          )}
          
          <Divider/>

          <div style={{background: '#fafafa', padding: 24, borderRadius: 8}}>
            <h3>Estado del Proceso</h3>
            <p>El período se valida con los datos incluidos en el archivo. Si hay inconsistencias o múltiples períodos, la importación las reportará.</p>
          </div>
        </>
    </PageApp>
}   

export default ImportarNomina;
